import './coapplicantPreviousEmploymentPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Disclosure } from '../../../components/Disclosure/Disclosure';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const {
    computed: { isCoapplicantPersisted },
    coapplicantPreviousIncomeEmployerName,
    coapplicantPreviousIncomeEmploymentType,
    coapplicantPreviousIncomeGrossAnnual,
    coapplicantPreviousIncomeJobTitle,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantPreviousIncomeEmployerName,
    setCoapplicantPreviousIncomeEmploymentType,
    setCoapplicantPreviousIncomeGrossAnnual,
    setCoapplicantPreviousIncomeJobTitle,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    formState: { errors, touchedFields, isDirty, isValid },
  } = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      previousEmployerNameValue: coapplicantPreviousIncomeEmployerName,
      previousEmploymentTypeValue: coapplicantPreviousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: toCurrencyString(
        coapplicantPreviousIncomeGrossAnnual,
      ),
      previousJobTitleValue: coapplicantPreviousIncomeJobTitle,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const shouldShowEmployedFields = employedTypes.includes(
    watch('previousEmploymentTypeValue'),
  );

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),

    onChange: (): void => {
      // the employmentType selection determines whether the employerName and jobTitle fields are rendered.
      // because the fields are conditionally required, we only revalidate the necessary form fields.
      const fieldsToRevalidate = shouldShowEmployedFields
        ? ([
            'previousEmployerNameValue',
            'previousJobTitleValue',
            'previousGrossAnnualIncomeValue',
          ] as const)
        : (['previousGrossAnnualIncomeValue'] as const);

      // only revalidate the fields that have been touched by the user as we normally validate onBlur.
      // this is to prevent the form from showing errors before the user has interacted with the form.
      fieldsToRevalidate
        .filter((field): boolean => touchedFields[field])
        .forEach((field): void => {
          trigger(field);
        });
    },
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      previousEmploymentTypeValue,
      previousEmployerNameValue,
      previousJobTitleValue,
      previousGrossAnnualIncomeValue,
    } = values;

    if (
      !previousEmploymentTypeValue ||
      (shouldShowEmployedFields && !previousEmployerNameValue) ||
      (shouldShowEmployedFields && !previousJobTitleValue) ||
      !previousGrossAnnualIncomeValue
    ) {
      return;
    }

    setCoapplicantPreviousIncomeEmploymentType(previousEmploymentTypeValue);
    setCoapplicantPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);

    if (shouldShowEmployedFields) {
      setCoapplicantPreviousIncomeEmployerName(previousEmployerNameValue);
      setCoapplicantPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setCoapplicantPreviousIncomeEmployerName(null);
      setCoapplicantPreviousIncomeJobTitle(null);
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-previous-employment-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantPreviousEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantPreviousEmployment.form.h1
                }
              >
                {t('CoapplicantPreviousEmploymentPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantPreviousEmployment.form.h2
                }
              >
                {t('CoapplicantPreviousEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantPreviousEmployment.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register(
                      'previousEmploymentTypeValue',
                      employmentTypeValidation,
                    )}
                    error={errors.previousEmploymentTypeValue}
                    errorTestId={
                      locator.pages.coapplicantPreviousEmployment.form
                        .employmentTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantPreviousEmployment.form
                        .employmentTypeSelect
                    }
                    label={t(
                      'CoapplicantPreviousEmploymentPage.employmentType',
                    )}
                    options={previousEmploymentTypeOptions}
                  />
                </div>

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'previousEmployerNameValue',
                        employerNameValidation,
                      )}
                      data-testid={
                        locator.pages.coapplicantPreviousEmployment.form
                          .employerNameInput
                      }
                      error={errors.previousEmployerNameValue}
                      errorTestId={
                        locator.pages.coapplicantPreviousEmployment.form
                          .employerNameInputError
                      }
                      label={t(
                        'CoapplicantPreviousEmploymentPage.employerName',
                      )}
                    />
                  </div>
                )}

                {shouldShowEmployedFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('previousJobTitleValue', jobTitleValidation)}
                      data-testid={
                        locator.pages.coapplicantPreviousEmployment.form
                          .jobTitleInput
                      }
                      error={errors.previousJobTitleValue}
                      errorTestId={
                        locator.pages.coapplicantPreviousEmployment.form
                          .jobTitleInputError
                      }
                      label={t('CoapplicantPreviousEmploymentPage.jobTitle')}
                    />
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <NumberInput
                    {...register(
                      'previousGrossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    data-testid={
                      locator.pages.coapplicantPreviousEmployment.form
                        .grossAnnualIncomeInput
                    }
                    error={errors.previousGrossAnnualIncomeValue}
                    errorTestId={
                      locator.pages.coapplicantPreviousEmployment.form
                        .grossAnnualIncomeInputError
                    }
                    label={t(
                      'CoapplicantPreviousEmploymentPage.grossAnnualIncome',
                    )}
                    currency
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantPreviousEmployment.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantPreviousEmploymentPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={
                  locator.pages.coapplicantPreviousEmployment.form
                    .incomeDisclosure
                }
              >
                <Disclosure i18nKey="disclosures.coapplicantIncome" />
              </p>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantPreviousEmployment.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantPreviousEmploymentPage';
